/**
 * The editor-dock field a click on the drawing copies text into.
 *
 * While an SME fills a form in the editor dock, a click on a click target writes the
 * target's text into the field that has focus, so a tag or a line number on the sheet
 * reaches the form without being retyped. The canvas keeps the focus where it is for
 * that press; this module says which fields take a copy, follows the focused one, and
 * writes the text the way typing would.
 */
import { useEffect, useState } from 'react';

/** A dock field that takes a copied text. */
export type CopyField = HTMLInputElement | HTMLTextAreaElement;

// `input.type` reads "text" for an input with no type attribute, so these four cover
// every typed-text input the dock forms render.
const COPYABLE_INPUT_TYPES = new Set(['text', 'search', 'number']);

/**
 * Whether a copied text may go into this element: an enabled, writable text field or
 * textarea in the dock view on show. A select, a checkbox and anything outside the dock,
 * the rail search and the comment box among them, take no copy.
 */
export function isCopyableField(element: EventTarget | null): element is CopyField {
  const isTextField = element instanceof HTMLTextAreaElement
    || (element instanceof HTMLInputElement && COPYABLE_INPUT_TYPES.has(element.type));
  if (!isTextField || element.disabled || element.readOnly || !element.isConnected) return false;
  // A covered view stays mounted under `hidden` with its draft, and takes no copy.
  return element.closest('[data-editor-dock]:not([hidden])') !== null;
}

/**
 * The copyable field that has focus, or null. Focus moving between fields reports the
 * new one; focus leaving for anything else reports null.
 */
export function useFocusedEditorField(): CopyField | null {
  const [focusedField, setFocusedField] = useState<CopyField | null>(() =>
    isCopyableField(document.activeElement) ? document.activeElement : null,
  );

  useEffect(() => {
    const followFocus = (event: FocusEvent) => {
      // A focusout names the element focus moves to, so a move between two fields
      // never reports null in between.
      const nextFocus = event.type === 'focusin' ? event.target : event.relatedTarget;
      setFocusedField(isCopyableField(nextFocus) ? nextFocus : null);
    };
    document.addEventListener('focusin', followFocus);
    document.addEventListener('focusout', followFocus);
    return () => {
      document.removeEventListener('focusin', followFocus);
      document.removeEventListener('focusout', followFocus);
    };
  }, []);

  // A dock that closes, or a view covered by another, can take the focused field with it
  // without a focusout, so every commit checks the field still takes a copy.
  // eslint-disable-next-line react-hooks/exhaustive-deps -- runs after every commit by design; it sets state only while the field fails the rule, so it settles in one pass
  useEffect(() => {
    if (focusedField && !isCopyableField(focusedField)) setFocusedField(null);
  });

  return focusedField;
}

/** Copied text reads as one line, as the canvas's hover label shows it. */
function collapseWhitespace(text: string): string {
  return text.replace(/\s+/g, ' ').trim();
}

/**
 * Writes `text` into `field` as typing would, and reports whether the value changed.
 *
 * The text goes in at the caret and replaces a selection, after one space unless the
 * text before the caret is empty or already ends in whitespace. The caret then sits
 * after the copy, so the next click appends there. A number field takes a parsable
 * number as its whole value; any other text leaves it unchanged, since the browser
 * would turn it into an empty value and the form would store null.
 *
 * The value goes through the native setter and a bubbling `input` event, because a
 * React controlled input ignores a plain `value` assignment: the event runs the
 * field's own `onChange`, so every form takes the copy through its existing handler.
 */
export function copyIntoField(field: CopyField, text: string): boolean {
  const copiedText = collapseWhitespace(text);
  if (!copiedText) return false;

  let nextValue: string;
  let caret: number | null = null;
  if (field instanceof HTMLInputElement && field.type === 'number') {
    if (!Number.isFinite(Number(copiedText))) return false;
    nextValue = copiedText;
  } else {
    const selectionStart = field.selectionStart ?? field.value.length;
    const selectionEnd = field.selectionEnd ?? selectionStart;
    const textBefore = field.value.slice(0, selectionStart);
    const separator = textBefore === '' || /\s$/.test(textBefore) ? '' : ' ';
    const inserted = separator + copiedText;
    nextValue = textBefore + inserted + field.value.slice(selectionEnd);
    caret = selectionStart + inserted.length;
  }
  if (nextValue === field.value) return false;

  const prototype = field instanceof HTMLTextAreaElement ? HTMLTextAreaElement.prototype : HTMLInputElement.prototype;
  Object.getOwnPropertyDescriptor(prototype, 'value')!.set!.call(field, nextValue);
  field.dispatchEvent(new Event('input', { bubbles: true }));
  if (caret !== null) field.setSelectionRange(caret, caret);
  return true;
}
